/**
 * Fleet Export Module - PDF generation for fleet-wide reports
 *
 * Provides comprehensive fleet status documentation for DPA oversight,
 * surveyor audits, and management reviews.
 *
 * Report Types:
 * - Fleet Status Report: Overview of all vessels with RAG status
 * - Certificate Expiry Report: Fleet-wide certificate tracking
 * - Shore Edit Audit Report: All shore edits with acknowledgement status
 * - Escalation Report: Outstanding notifications requiring action
 *
 * @see FLEET-DASHBOARD-DESIGN.md Section 3.6
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  createPDF,
  getPageDimensions,
  setFont,
  checkPageBreak,
  addReportHeader,
  addReportFooter,
  addSectionHeading,
  savePDF,
  handlePDFError,
  PDF_CONFIG
} from './pdfCore';

/**
 * Export Fleet Status Report as PDF
 * Shows all vessels with compliance RAG status
 *
 * @param {Object} fleetData - Fleet data from useFleetData hook
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportFleetStatusPDF(fleetData, options = {}) {
  try {
    const {
      companyName = 'Fleet Management',
      reportTitle = 'Fleet Status Report',
      generatedBy = 'DPA'
    } = options;

    const { vessels = [], fleetSummary = {} } = fleetData;

    if (!vessels || vessels.length === 0) {
      alert('No vessel data found to export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth, pageWidth } = getPageDimensions(pdf);

    let currentY = margin;

    // Header
    currentY = addReportHeader(pdf, currentY, {
      title: reportTitle,
      vesselName: companyName,
      subtitle: `Generated: ${new Date().toLocaleDateString('en-GB')} by ${generatedBy}`
    });

    // Fleet Summary Section
    currentY = addSectionHeading(pdf, currentY, 'Fleet Summary', 'heading');

    setFont(pdf, 'body');
    const summaryData = [
      ['Total Vessels', vessels.length.toString()],
      ['Compliant (Green)', (fleetSummary.compliant || 0).toString()],
      ['Attention Required (Amber)', (fleetSummary.attention || 0).toString()],
      ['Action Required (Red)', (fleetSummary.actionRequired || 0).toString()],
      ['Expiring Certificates', (fleetSummary.totalExpiringCerts || 0).toString()]
    ];

    autoTable(pdf, {
      startY: currentY,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: {
        fillColor: [51, 65, 85], // slate-700
        fontSize: 8
      },
      bodyStyles: {
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 30, halign: 'center' }
      },
      margin: { left: margin, right: margin },
      tableWidth: 90
    });

    currentY = pdf.lastAutoTable.finalY + PDF_CONFIG.sectionSpacing;

    // Vessels Table
    currentY = addSectionHeading(pdf, currentY, 'Vessel Status', 'heading');

    const vesselTableData = vessels.map(vessel => [
      vessel.name || 'Unknown',
      vessel.ragStatus?.toUpperCase() || 'N/A',
      (vessel.expiringCerts || 0).toString(),
      (vessel.overdueJobs || 0).toString(),
      (vessel.openDefects || 0).toString()
    ]);

    autoTable(pdf, {
      startY: currentY,
      head: [['Vessel Name', 'Status', 'Expiring Certs', 'Overdue Jobs', 'Open Defects']],
      body: vesselTableData,
      theme: 'striped',
      headStyles: {
        fillColor: [51, 65, 85],
        fontSize: 8
      },
      bodyStyles: {
        fontSize: 8
      },
      didParseCell: function(data) {
        // Color code RAG status column
        if (data.column.index === 1 && data.section === 'body') {
          const status = data.cell.raw?.toLowerCase();
          if (status === 'red') {
            data.cell.styles.textColor = [220, 38, 38]; // red-600
            data.cell.styles.fontStyle = 'bold';
          } else if (status === 'amber') {
            data.cell.styles.textColor = [217, 119, 6]; // amber-600
            data.cell.styles.fontStyle = 'bold';
          } else if (status === 'green') {
            data.cell.styles.textColor = [22, 163, 74]; // green-600
          }
        }
      },
      margin: { left: margin, right: margin }
    });

    // Footer
    addReportFooter(pdf, {
      leftText: companyName,
      centerText: 'CONFIDENTIAL - Fleet Management Use Only',
      includePageNumbers: true
    });

    // Save
    const filename = `Fleet_Status_${new Date().toISOString().split('T')[0]}.pdf`;
    savePDF(pdf, filename);

    return true;
  } catch (error) {
    handlePDFError(error, 'Fleet Status Report');
    return false;
  }
}

/**
 * Export Certificate Expiry Report as PDF
 * Shows all certificates across fleet with expiry dates
 *
 * @param {Array} certificates - Array of certificate objects
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportCertificateExpiryPDF(certificates, options = {}) {
  try {
    const {
      companyName = 'Fleet Management',
      reportTitle = 'Certificate Expiry Report',
      generatedBy = 'DPA',
      includeValid = true
    } = options;

    if (!certificates || certificates.length === 0) {
      alert('No certificate data found to export.');
      return false;
    }

    // Filter and sort by expiry
    let filteredCerts = certificates;
    if (!includeValid) {
      filteredCerts = certificates.filter(c => {
        const days = getDaysUntilExpiry(c.expiryDate);
        return days !== null && days <= 60;
      });
    }

    filteredCerts.sort((a, b) => {
      if (!a.expiryDate) return 1;
      if (!b.expiryDate) return -1;
      return new Date(a.expiryDate) - new Date(b.expiryDate);
    });

    const pdf = createPDF();
    const { margin } = getPageDimensions(pdf);

    let currentY = margin;

    // Header
    currentY = addReportHeader(pdf, currentY, {
      title: reportTitle,
      vesselName: companyName,
      subtitle: `Generated: ${new Date().toLocaleDateString('en-GB')} by ${generatedBy}`
    });

    // Summary counts
    const expired = filteredCerts.filter(c => getDaysUntilExpiry(c.expiryDate) < 0).length;
    const critical = filteredCerts.filter(c => {
      const days = getDaysUntilExpiry(c.expiryDate);
      return days !== null && days >= 0 && days <= 14;
    }).length;
    const warning = filteredCerts.filter(c => {
      const days = getDaysUntilExpiry(c.expiryDate);
      return days !== null && days > 14 && days <= 60;
    }).length;

    currentY = addSectionHeading(pdf, currentY, 'Summary', 'heading');

    const summaryData = [
      ['Total Certificates', filteredCerts.length.toString()],
      ['Expired', expired.toString()],
      ['Critical (< 14 days)', critical.toString()],
      ['Warning (< 60 days)', warning.toString()]
    ];

    autoTable(pdf, {
      startY: currentY,
      head: [['Status', 'Count']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [51, 65, 85], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: margin },
      tableWidth: 90
    });

    currentY = pdf.lastAutoTable.finalY + PDF_CONFIG.sectionSpacing;

    // Certificates Table
    currentY = addSectionHeading(pdf, currentY, 'Certificate Details', 'heading');

    const certTableData = filteredCerts.map(cert => {
      const days = getDaysUntilExpiry(cert.expiryDate);
      let status = 'Valid';
      if (days === null) status = 'No Expiry';
      else if (days < 0) status = 'EXPIRED';
      else if (days <= 14) status = `${days}d - CRITICAL`;
      else if (days <= 60) status = `${days}d - WARNING`;

      return [
        cert.vesselName || 'Unknown',
        cert.name || 'Unknown',
        cert.certificateNumber || 'N/A',
        cert.expiryDate ? new Date(cert.expiryDate).toLocaleDateString('en-GB') : 'N/A',
        status
      ];
    });

    autoTable(pdf, {
      startY: currentY,
      head: [['Vessel', 'Certificate', 'Number', 'Expiry Date', 'Status']],
      body: certTableData,
      theme: 'striped',
      headStyles: { fillColor: [51, 65, 85], fontSize: 8 },
      bodyStyles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 50 },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
        4: { cellWidth: 30 }
      },
      didParseCell: function(data) {
        if (data.column.index === 4 && data.section === 'body') {
          const status = data.cell.raw;
          if (status.includes('EXPIRED')) {
            data.cell.styles.textColor = [220, 38, 38];
            data.cell.styles.fontStyle = 'bold';
          } else if (status.includes('CRITICAL')) {
            data.cell.styles.textColor = [220, 38, 38];
          } else if (status.includes('WARNING')) {
            data.cell.styles.textColor = [217, 119, 6];
          }
        }
      },
      margin: { left: margin, right: margin }
    });

    // Footer
    addReportFooter(pdf, {
      leftText: companyName,
      centerText: 'CONFIDENTIAL - Fleet Management Use Only',
      includePageNumbers: true
    });

    const filename = `Certificate_Expiry_${new Date().toISOString().split('T')[0]}.pdf`;
    savePDF(pdf, filename);

    return true;
  } catch (error) {
    handlePDFError(error, 'Certificate Expiry Report');
    return false;
  }
}

/**
 * Export Shore Edit Audit Report as PDF
 * Shows all shore edits with acknowledgement status
 *
 * @param {Array} auditTrail - Array of audit entries
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportShoreEditAuditPDF(auditTrail, options = {}) {
  try {
    const {
      companyName = 'Fleet Management',
      reportTitle = 'Shore Edit Audit Report',
      generatedBy = 'DPA',
      dateFrom = null,
      dateTo = null
    } = options;

    if (!auditTrail || auditTrail.length === 0) {
      alert('No audit data found to export.');
      return false;
    }

    // Filter by date if specified
    let filteredAudit = auditTrail;
    if (dateFrom) {
      filteredAudit = filteredAudit.filter(a =>
        new Date(a.editedAt) >= new Date(dateFrom)
      );
    }
    if (dateTo) {
      filteredAudit = filteredAudit.filter(a =>
        new Date(a.editedAt) <= new Date(dateTo)
      );
    }

    // Sort by date descending
    filteredAudit.sort((a, b) =>
      new Date(b.editedAt) - new Date(a.editedAt)
    );

    const pdf = createPDF();
    const { margin } = getPageDimensions(pdf);

    let currentY = margin;

    // Header
    currentY = addReportHeader(pdf, currentY, {
      title: reportTitle,
      vesselName: companyName,
      subtitle: `Generated: ${new Date().toLocaleDateString('en-GB')} by ${generatedBy}`
    });

    // Summary
    const acknowledged = filteredAudit.filter(a => a.vesselAcknowledged).length;
    const pending = filteredAudit.length - acknowledged;

    currentY = addSectionHeading(pdf, currentY, 'Summary', 'heading');

    autoTable(pdf, {
      startY: currentY,
      head: [['Metric', 'Count']],
      body: [
        ['Total Shore Edits', filteredAudit.length.toString()],
        ['Acknowledged by Vessel', acknowledged.toString()],
        ['Pending Acknowledgement', pending.toString()]
      ],
      theme: 'grid',
      headStyles: { fillColor: [51, 65, 85], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: margin },
      tableWidth: 90
    });

    currentY = pdf.lastAutoTable.finalY + PDF_CONFIG.sectionSpacing;

    // Audit Trail Table
    currentY = addSectionHeading(pdf, currentY, 'Edit History', 'heading');

    const auditTableData = filteredAudit.map(entry => [
      entry.editedAt ? new Date(entry.editedAt).toLocaleDateString('en-GB') : 'N/A',
      entry.module || 'Unknown',
      entry.itemName || entry.field || 'Unknown',
      entry.editedBy?.name || 'Unknown',
      entry.vesselAcknowledged ? 'Yes' : 'PENDING'
    ]);

    autoTable(pdf, {
      startY: currentY,
      head: [['Date', 'Module', 'Item', 'Edited By', 'Acknowledged']],
      body: auditTableData,
      theme: 'striped',
      headStyles: { fillColor: [51, 65, 85], fontSize: 8 },
      bodyStyles: { fontSize: 7 },
      didParseCell: function(data) {
        if (data.column.index === 4 && data.section === 'body') {
          if (data.cell.raw === 'PENDING') {
            data.cell.styles.textColor = [217, 119, 6];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      margin: { left: margin, right: margin }
    });

    // Footer
    addReportFooter(pdf, {
      leftText: companyName,
      centerText: 'ISM Code Section 9 - Audit Trail Documentation',
      includePageNumbers: true
    });

    const filename = `Shore_Edit_Audit_${new Date().toISOString().split('T')[0]}.pdf`;
    savePDF(pdf, filename);

    return true;
  } catch (error) {
    handlePDFError(error, 'Shore Edit Audit Report');
    return false;
  }
}

/**
 * Export Escalation Report as PDF
 * Shows all notifications requiring escalation action
 *
 * @param {Array} notifications - Array of notification objects
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportEscalationReportPDF(notifications, options = {}) {
  try {
    const {
      companyName = 'Fleet Management',
      reportTitle = 'Escalation Report',
      generatedBy = 'DPA'
    } = options;

    // Filter to unacknowledged notifications only
    const unacknowledged = (notifications || []).filter(n =>
      !n.acknowledgedAt && n.type === 'shore_edit'
    );

    if (unacknowledged.length === 0) {
      alert('No outstanding escalations to report.');
      return false;
    }

    const pdf = createPDF();
    const { margin } = getPageDimensions(pdf);

    let currentY = margin;

    // Header
    currentY = addReportHeader(pdf, currentY, {
      title: reportTitle,
      vesselName: companyName,
      subtitle: `Generated: ${new Date().toLocaleDateString('en-GB')} by ${generatedBy}`
    });

    // Summary by escalation level
    const level1 = unacknowledged.filter(n =>
      (n.escalations || []).some(e => e.level === 1)
    ).length;
    const level2 = unacknowledged.filter(n =>
      (n.escalations || []).some(e => e.level === 2)
    ).length;
    const level3 = unacknowledged.filter(n =>
      (n.escalations || []).some(e => e.level === 3)
    ).length;

    currentY = addSectionHeading(pdf, currentY, 'Escalation Summary', 'heading');

    autoTable(pdf, {
      startY: currentY,
      head: [['Level', 'Description', 'Count']],
      body: [
        ['Level 1', 'Reminder Sent (48h)', level1.toString()],
        ['Level 2', 'DPA Alerted (7 days)', level2.toString()],
        ['Level 3', 'Compliance Flag (14 days)', level3.toString()]
      ],
      theme: 'grid',
      headStyles: { fillColor: [51, 65, 85], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: margin },
      tableWidth: 120
    });

    currentY = pdf.lastAutoTable.finalY + PDF_CONFIG.sectionSpacing;

    // Details Table
    currentY = addSectionHeading(pdf, currentY, 'Outstanding Notifications', 'heading');

    const detailsData = unacknowledged.map(n => {
      const maxLevel = Math.max(...(n.escalations || []).map(e => e.level), 0);
      const hoursSince = Math.round(
        (Date.now() - new Date(n.editedAt).getTime()) / (1000 * 60 * 60)
      );

      return [
        n.vesselName || 'Unknown',
        n.change?.module || 'Unknown',
        n.change?.itemName || 'Unknown',
        n.editedBy?.name || 'Unknown',
        `${hoursSince}h`,
        maxLevel > 0 ? `Level ${maxLevel}` : 'None'
      ];
    });

    autoTable(pdf, {
      startY: currentY,
      head: [['Vessel', 'Module', 'Item', 'Editor', 'Age', 'Escalation']],
      body: detailsData,
      theme: 'striped',
      headStyles: { fillColor: [51, 65, 85], fontSize: 8 },
      bodyStyles: { fontSize: 7 },
      didParseCell: function(data) {
        if (data.column.index === 5 && data.section === 'body') {
          const level = data.cell.raw;
          if (level.includes('3')) {
            data.cell.styles.textColor = [220, 38, 38];
            data.cell.styles.fontStyle = 'bold';
          } else if (level.includes('2')) {
            data.cell.styles.textColor = [217, 119, 6];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      margin: { left: margin, right: margin }
    });

    // Footer
    addReportFooter(pdf, {
      leftText: companyName,
      centerText: 'URGENT - Requires DPA Action',
      includePageNumbers: true
    });

    const filename = `Escalation_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    savePDF(pdf, filename);

    return true;
  } catch (error) {
    handlePDFError(error, 'Escalation Report');
    return false;
  }
}

/**
 * Helper: Calculate days until expiry
 */
function getDaysUntilExpiry(expiryDate) {
  if (!expiryDate) return null;
  const now = new Date();
  const expiry = new Date(expiryDate);
  return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
}
